import type { TFunction } from 'i18next';
import {
  ChevronDown,
  ChevronUp,
  Circle,
  CircleCheck,
  CirclePause,
  CircleX,
  Loader2,
  RotateCw,
} from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@shared/components/ui/Button';
import { cn } from '@shared/components/ui/cn';

import type {
  DataCategory,
  MessageRoute,
  MessageStatus,
  ProcessingFailure,
  ProcessingStep,
  WaitingState,
} from '../types';

export function formatWorkedFor(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) return `${String(total)}s`;
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes)}m ${String(seconds).padStart(2, '0')}s`;
}

function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes)}:${String(seconds).padStart(2, '0')}`;
}

export function dataLabel(t: TFunction<'ai-cio'>, categories: DataCategory[]): string {
  const investor = categories.includes('investor');
  const crm = categories.includes('crm');
  if (investor && crm) return t('processing.data.both', 'Investor and CRM data');
  if (crm) return t('processing.data.crm', 'CRM data');
  return t('processing.data.investor', 'Investor data');
}

export function doneLineSuffix(
  t: TFunction<'ai-cio'>,
  route: MessageRoute | undefined,
): string | null {
  if (!route || route.categories.length === 0) return null;
  const data = dataLabel(t, route.categories);
  return route.sharedWithProvider
    ? t('processing.suffixShared', '{{data}} shared with {{provider}}', {
        data,
        provider: route.sharedWithProvider,
      })
    : t('processing.suffixStayed', '{{data}} stayed in Investbanq', { data });
}

type Phase = 'working' | 'waiting' | 'done' | 'failed' | 'stopped';

function phaseOf(status: MessageStatus, waiting: WaitingState | undefined): Phase {
  if (status === 'streaming') return waiting ? 'waiting' : 'working';
  if (status === 'complete') return 'done';
  if (status === 'cancelled' || status === 'stopped') return 'stopped';
  if (status === 'queued') return 'working';
  return 'failed';
}

export interface ProcessingIndicatorProps {
  steps: ProcessingStep[];
  status: MessageStatus;
  waiting?: WaitingState;
  workedForMs?: number;
  route?: MessageRoute;
  failure?: ProcessingFailure;
  defaultExpanded?: boolean;
  onRetry?: () => void;
  onAnswerInternal?: () => void;
  className?: string;
}

export function ProcessingIndicator({
  steps,
  status,
  waiting,
  workedForMs,
  route,
  failure,
  defaultExpanded = false,
  onRetry,
  onAnswerInternal,
  className,
}: ProcessingIndicatorProps) {
  const { t } = useTranslation('ai-cio');
  const phase = phaseOf(status, waiting);
  const hasRouteStep = steps.some((s) => s.kind === 'route');
  const [expanded, setExpanded] = useState(defaultExpanded);
  const userToggled = useRef(false);
  const listId = useId();

  useEffect(() => {
    if (hasRouteStep && status === 'streaming' && !userToggled.current) setExpanded(true);
  }, [hasRouteStep, status]);

  const prevStatus = useRef(status);
  useEffect(() => {
    if (prevStatus.current === 'streaming' && status === 'complete') {
      userToggled.current = false;
      setExpanded(false);
    }
    prevStatus.current = status;
  }, [status]);

  const mountedAt = useRef(Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (phase !== 'working') return;
    const id = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, [phase]);
  const elapsed = now - mountedAt.current;

  const doneCount = steps.filter((s) => s.status === 'done').length;
  const failedIndex = steps.findIndex((s) => s.status === 'failed');
  const current = steps.find((s) => s.status === 'active');
  const suffix = doneLineSuffix(t, route);
  const worked = formatWorkedFor(workedForMs ?? 0);
  const routeStep = steps.find((s) => s.kind === 'route');
  const announcement =
    phase === 'done'
      ? `${t('processing.announceDone', 'Answer ready. Answered by {{model}}.', {
          model: route?.modelUsed.label ?? '',
        })}${suffix ? ` ${suffix}.` : ''}`
      : (routeStep?.label ?? '');

  const toggle = () => {
    userToggled.current = true;
    setExpanded((v) => !v);
  };
  const Chevron = expanded ? ChevronUp : ChevronDown;

  if (phase === 'done') {
    const doneLine = t('processing.doneLine', 'Worked for {{time}} · {{count}} steps', {
      count: steps.length,
      time: worked,
    });
    return (
      <div className={cn('flex flex-col gap-2', className)}>
        <button
          type="button"
          onClick={toggle}
          aria-expanded={expanded}
          aria-controls={listId}
          className="flex min-h-6 max-w-full items-center gap-2 rounded-md text-start text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          <CircleCheck className="size-4 shrink-0" aria-hidden />
          <span className="min-w-0 truncate">{suffix ? `${doneLine} · ${suffix}` : doneLine}</span>
          <Chevron className="size-4 shrink-0" aria-hidden />
        </button>
        {expanded && (
          <div id={listId} className="rounded-xl border bg-card p-4">
            <StepList steps={steps} />
          </div>
        )}
        <span className="sr-only" role="status">
          {announcement}
        </span>
      </div>
    );
  }

  const failed = phase === 'failed';
  const stopped = phase === 'stopped';
  const title =
    phase === 'working'
      ? t('processing.working', 'Working on your request')
      : phase === 'waiting'
        ? t('processing.waitingPolicy', 'Waiting for you · Data policy')
        : failed
          ? t('processing.failed', 'Couldn’t finish your request')
          : t('processing.stopped', 'Stopped by you');
  const showSteps = expanded || failed;

  return (
    <div
      className={cn(
        'rounded-xl border p-4',
        failed
          ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/40'
          : stopped
            ? 'border-border bg-muted'
            : 'border-border bg-card',
        className,
      )}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={showSteps}
        aria-controls={listId}
        className="flex min-h-6 w-full items-center gap-2 text-start"
      >
        {phase === 'working' && (
          <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" aria-hidden />
        )}
        {phase === 'waiting' && (
          <CirclePause className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        )}
        {failed && <CircleX className="size-4 shrink-0 text-red-600" aria-hidden />}
        {stopped && <CircleX className="size-4 shrink-0 text-muted-foreground" aria-hidden />}
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{title}</span>
        {phase === 'working' && elapsed >= 3000 && (
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {formatClock(elapsed)}
          </span>
        )}
        {phase === 'waiting' && (
          <span className="shrink-0 text-xs text-muted-foreground">
            {t('processing.stepsDone', '{{count}} steps done', { count: doneCount })}
          </span>
        )}
        <Chevron className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      </button>

      {failed && failedIndex >= 0 && (
        <p className="ps-6 pt-0.5 text-xs text-neutral-600 dark:text-neutral-300">
          {t('processing.failedAt', 'Failed at step {{step}} · {{time}}', {
            step: failedIndex + 1,
            time: worked,
          })}
        </p>
      )}
      {phase === 'working' && !expanded && current && (
        <p className="truncate ps-6 pt-0.5 text-xs text-muted-foreground">{current.label}</p>
      )}

      {showSteps && (
        <>
          <div
            className={cn('my-3 h-px', failed ? 'bg-red-200 dark:bg-red-900' : 'bg-border')}
            aria-hidden
          />
          <StepList id={listId} steps={steps} onFailedTile={failed} />
        </>
      )}

      {(failed || stopped) && (onRetry || (failure?.offerInternalFallback && onAnswerInternal)) && (
        <div className="flex flex-wrap gap-2 pt-3">
          {onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RotateCw aria-hidden />
              {t('processing.retry', 'Retry')}
            </Button>
          )}
          {failure?.offerInternalFallback && onAnswerInternal && (
            <Button variant="ghost" size="sm" onClick={onAnswerInternal}>
              {t('processing.answerInternal', 'Answer with the Investbanq model')}
            </Button>
          )}
        </div>
      )}
      <span className="sr-only" role="status" aria-live="assertive">
        {announcement}
      </span>
    </div>
  );
}

function StepList({
  id,
  steps,
  onFailedTile = false,
}: {
  id?: string;
  steps: ProcessingStep[];
  onFailedTile?: boolean;
}) {
  return (
    <ol id={id} className="flex flex-col">
      {steps.map((step) => (
        <StepRow key={step.id} step={step} onFailedTile={onFailedTile} />
      ))}
    </ol>
  );
}

function StepRow({ step, onFailedTile }: { step: ProcessingStep; onFailedTile: boolean }) {
  const emphasized = step.status === 'active' || step.status === 'failed';
  const showDuration =
    (step.status === 'done' || step.status === 'failed') &&
    step.durationMs !== undefined &&
    step.durationMs >= 1000;
  return (
    <li className="flex items-start gap-2 py-1">
      <span className="flex h-5 w-4 shrink-0 items-center justify-center">
        {step.status === 'active' ? (
          <Loader2 className="size-4 animate-spin text-muted-foreground" aria-hidden />
        ) : step.status === 'done' ? (
          <CircleCheck className="size-4 text-muted-foreground" aria-hidden />
        ) : step.status === 'failed' ? (
          <CircleX className="size-4 text-red-600" aria-hidden />
        ) : (
          <Circle className="size-4 text-muted-foreground" aria-hidden />
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span
          className={cn(
            'truncate text-sm',
            emphasized
              ? 'font-medium text-foreground'
              : onFailedTile
                ? 'text-neutral-600 dark:text-neutral-300'
                : 'text-muted-foreground',
          )}
        >
          {step.label}
        </span>
        {step.detail && (
          <span
            className={cn(
              'truncate text-xs',
              step.status === 'failed' ? 'text-red-700 dark:text-red-400' : 'text-muted-foreground',
            )}
          >
            {step.detail}
          </span>
        )}
      </span>
      {showDuration && (
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
          {formatWorkedFor(step.durationMs ?? 0)}
        </span>
      )}
    </li>
  );
}
